import { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button, Descriptions, Drawer, Select, Space, Table, Tag, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import * as uiApi from '@/api/uiAutomation'
import { useProjectStore } from '@/stores/project'
import type { UIArtifactFile, UIExecutionDetail, UIExecutionResult, UIExecutionSummary } from '@/types'
import '../../styles/aurora-page.css'

const { Title, Text } = Typography

/** 执行结果: 1=pending/2=running/3=passed/4=failed/5=error */
const resultMap: Record<UIExecutionResult, { text: string; color: string }> = {
  1: { text: '排队中', color: 'default' },
  2: { text: '执行中', color: 'processing' },
  3: { text: '成功', color: 'success' },
  4: { text: '失败', color: 'error' },
  5: { text: '错误', color: 'warning' },
}

export default function UIExecutions() {
  const navigate = useNavigate()
  const { currentProject } = useProjectStore()
  const projectId = currentProject?.id || 0

  // 列表状态
  const [executions, setExecutions] = useState<UIExecutionSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  // 五期 #6：触发来源筛选（0=全部/1=手动/2=定时）
  const [triggerFilter, setTriggerFilter] = useState(0)

  // 详情抽屉（轮询）
  const [detailOpen, setDetailOpen] = useState(false)
  const [detail, setDetail] = useState<UIExecutionDetail | null>(null)
  const [artifacts, setArtifacts] = useState<UIArtifactFile[]>([])
  const logRef = useRef<HTMLPreElement>(null)
  // 三期 F：日志级别过滤（文本日志行格式 "[HH:MM:SS] [level] message"）
  const [logLevel, setLogLevel] = useState<'all' | 'warning' | 'error'>('all')

  /** 按级别过滤后的日志（all=全部 / warning=警告及以上 / error=仅错误） */
  const filteredLog = useMemo(() => {
    const raw = detail?.log || ''
    if (!raw || logLevel === 'all') return raw
    return raw
      .split('\n')
      .filter((line) => {
        const m = line.match(/\]\s*\[(\w+)\]/)
        const lv = (m?.[1] || 'info').toLowerCase()
        if (logLevel === 'warning') return lv === 'warning' || lv === 'error'
        return lv === 'error'
      })
      .join('\n')
  }, [detail?.log, logLevel])

  const fetchExecutions = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const res = await uiApi.listExecutions({
        project_id: projectId,
        page,
        page_size: pageSize,
        trigger_source: triggerFilter || undefined,
      })
      setExecutions(res.data)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [projectId, page, pageSize, triggerFilter])

  useEffect(() => {
    fetchExecutions()
  }, [fetchExecutions])

  // ============ 详情加载（执行中每 2s 轮询日志与结果；已结束只拉一次） ============
  useEffect(() => {
    if (!detailOpen || !detail) return
    let stopped = false
    const load = async () => {
      try {
        const d = await uiApi.getExecution(detail.id)
        if (stopped) return
        setDetail(d)
        if (d.result !== 1 && d.result !== 2) {
          // 执行结束后加载产物文件列表（截图/trace）
          try {
            const files = await uiApi.listExecutionArtifacts(d.id)
            if (!stopped) setArtifacts(files)
          } catch {
            if (!stopped) setArtifacts([])
          }
          return true // 已结束，停止轮询
        }
      } catch {
        // 轮询失败静默重试
      }
      return false
    }
    load()
    const timer = window.setInterval(async () => {
      const finished = await load()
      if (finished) window.clearInterval(timer)
    }, 2000)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [detailOpen, detail?.id])

  // 日志自动滚动到底部
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [detail?.log])

  const openDetail = (record: UIExecutionSummary) => {
    setDetail({
      ...record,
      executor_name: '',
      log: '',
      update_time: record.create_time,
    })
    setArtifacts([])
    setDetailOpen(true)
  }

  const copyLog = async () => {
    if (!detail?.log) return
    try {
      await navigator.clipboard.writeText(detail.log)
      message.success('日志已复制')
    } catch {
      message.error('复制失败，请手动选择复制')
    }
  }

  const columns: ColumnsType<UIExecutionSummary> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '用例名称',
      dataIndex: 'script_name',
      width: 240,
      render: (v: string, r: UIExecutionSummary) => (
        <Space size={6}>
          <Text style={{ fontWeight: 600, color: 'var(--au-slate)' }}>{v || '-'}</Text>
          {/* 四期 M：定时任务触发的执行标记「定时触发」 */}
          {r.trigger_source === 2 ? <Tag color="purple">定时触发</Tag> : null}
        </Space>
      ),
    },
    {
      title: '执行环境',
      dataIndex: 'run_env',
      width: 160,
      render: (v: string) => (
        <Tag color="geekblue">{v || '默认地址'}</Tag>
      ),
    },
    {
      title: '结果',
      dataIndex: 'result',
      width: 100,
      render: (v: UIExecutionResult) => {
        const s = resultMap[v] || resultMap[1]
        return <Tag color={s.color}>{s.text}</Tag>
      },
    },
    {
      title: '耗时',
      dataIndex: 'duration_ms',
      width: 100,
      render: (v: number) => (
        <span style={{ fontSize: 13 }}>{v > 0 ? `${(v / 1000).toFixed(1)}s` : '-'}</span>
      ),
    },
    {
      title: 'AI 调用',
      dataIndex: 'ai_calls',
      width: 96,
      render: (v: number, r: UIExecutionSummary) => (
        <span style={{ fontSize: 13 }}>
          {v > 0 ? `${v} 次` : '-'}
          {r.cache_hits > 0 ? (
            <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>（缓存 {r.cache_hits}）</span>
          ) : null}
        </span>
      ),
    },
    {
      title: '自愈',
      dataIndex: 'heal_count',
      width: 80,
      render: (v: number) =>
        v > 0 ? <Tag color="orange">自愈 {v} 次</Tag> : <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>-</span>,
    },
    {
      title: '报告',
      dataIndex: 'report_id',
      width: 100,
      render: (v: number | null) =>
        v ? (
          // 跳转测试报告页面并自动打开该报告详情
          <Button type="link" size="small" onClick={() => navigate(`/reports?id=${v}`)}>查看报告</Button>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>-</span>
        ),
    },
    {
      title: '执行时间',
      dataIndex: 'create_time',
      width: 170,
      render: (v: string) => (
        <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>
          {v ? new Date(v).toLocaleString('zh-CN', { hour12: false }) : '-'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_, r: UIExecutionSummary) => (
        <Button type="link" size="small" onClick={() => openDetail(r)}>详情</Button>
      ),
    },
  ]

  const running = detail ? detail.result === 1 || detail.result === 2 : false

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>执行中心</Title>
          <Tag color="orange">Playwright 执行记录</Tag>
          <Tag color="blue">截图 / Trace 产物</Tag>
        </div>
        <Space>
          {/* 五期 #6：触发来源筛选（手动/定时） */}
          <Select
            value={triggerFilter}
            style={{ width: 130 }}
            onChange={(v) => {
              setTriggerFilter(v)
              setPage(1)
            }}
            options={[
              { value: 0, label: '全部来源' },
              { value: 1, label: '手动执行' },
              { value: 2, label: '定时触发' },
            ]}
          />
          <Button onClick={fetchExecutions}>刷新</Button>
        </Space>
      </div>

      <div className="content-card">
        <Table
          columns={columns}
          dataSource={executions}
          rowKey="id"
          loading={loading}
          scroll={{ y: '100%' }}
          pagination={{
            current: page,
            total,
            pageSize,
            showSizeChanger: true,
            showTotal: (t: number) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
        />
      </div>

      {/* ============ 执行详情抽屉 ============ */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>执行详情</span>
            {detail ? <Tag color={resultMap[detail.result]?.color || 'default'}>{resultMap[detail.result]?.text || '未知'}</Tag> : null}
            {detail && detail.duration_ms > 0 ? (
              <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>{(detail.duration_ms / 1000).toFixed(1)}s</span>
            ) : null}
          </div>
        }
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={720}
        rootClassName="aurora-edit-drawer"
      >
        {detail ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Descriptions
              column={2}
              size="small"
              labelStyle={{ fontSize: 13, color: 'var(--au-slate-muted)', width: 90 }}
            >
              <Descriptions.Item label="用例">{detail.script_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="执行环境">{detail.run_env || '默认地址'}</Descriptions.Item>
              <Descriptions.Item label="执行人">{detail.executor_name || '自动化'}</Descriptions.Item>
              <Descriptions.Item label="执行时间">
                {detail.create_time ? new Date(detail.create_time).toLocaleString('zh-CN', { hour12: false }) : '-'}
              </Descriptions.Item>
            </Descriptions>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ fontSize: 13 }}>执行日志{running ? '（实时刷新中）' : ''}</Text>
                <Space size={8}>
                  <Select
                    size="small"
                    value={logLevel}
                    onChange={setLogLevel}
                    style={{ width: 140 }}
                    options={[
                      { value: 'all', label: '全部日志' },
                      { value: 'warning', label: '警告及以上' },
                      { value: 'error', label: '仅错误' },
                    ]}
                  />
                  {!running && detail.log ? (
                    <Button type="link" size="small" onClick={copyLog}>复制日志</Button>
                  ) : null}
                </Space>
              </div>
              <pre
                ref={logRef}
                style={{
                  margin: 0,
                  height: 340,
                  overflow: 'auto',
                  borderRadius: 10,
                  padding: '12px 14px',
                  fontSize: 12,
                  lineHeight: '20px',
                  fontFamily: 'Consolas, Menlo, monospace',
                  background: '#10192b',
                  color: '#c8d6e5',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {filteredLog || '暂无日志'}
              </pre>
            </div>

            <div>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>执行产物</Text>
              {artifacts.length === 0 ? (
                <span style={{ fontSize: 13, color: 'var(--au-slate-muted)' }}>
                  {running ? '执行结束后生成截图 / Trace 产物' : '无产物文件'}
                </span>
              ) : (
                <>
                  <Space direction="vertical" style={{ width: '100%' }} size={4}>
                    {artifacts.map((f) => (
                      <div
                        key={f.url}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderRadius: 8, background: 'var(--au-surface, rgba(0,0,0,0.02))' }}
                      >
                        {/* 四期 K：trace.zip 走内置 Trace Viewer 在线回放，其余产物直接打开 */}
                        <a
                          href={/\.zip$/i.test(f.name) ? `/trace-viewer/index.html?trace=${f.url}` : f.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 13 }}
                        >
                          {f.name}
                        </a>
                        <span style={{ fontSize: 12, color: 'var(--au-slate-muted)' }}>
                          {f.size > 1024 * 1024 ? `${(f.size / 1024 / 1024).toFixed(1)} MB` : `${(f.size / 1024).toFixed(1)} KB`}
                        </span>
                      </div>
                    ))}
                  </Space>
                  {/* 截图预览（断言截图 / 失败截图），点击查看原图 */}
                  {artifacts.some((f) => /\.(png|jpe?g|webp)$/i.test(f.name)) && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {artifacts
                        .filter((f) => /\.(png|jpe?g|webp)$/i.test(f.name))
                        .map((f) => (
                          <a key={f.url} href={f.url} target="_blank" rel="noreferrer" title={f.name}>
                            <img
                              src={f.url}
                              alt={f.name}
                              style={{ width: 108, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--au-border)' }}
                            />
                          </a>
                        ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  )
}
